import { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useAuth } from "./AuthProvider";
import api from "../api/api";

const InstalledAppsContext = createContext();

/**
 * InstalledAppsProvider
 *
 * Centralised context that tracks which modules are installed for the
 * current tenant. The Sidebar (menu filtering), ProtectedRoute (route-level
 * gating) and the index redirect all consume this, so /apps/my-apps/ is
 * requested once rather than by each of them.
 *
 * Auto-refreshes when:
 *   • the user logs in (token changes)
 *   • an install/uninstall triggers the existing `company-updated` event
 */
const InstalledAppsProvider = ({ children }) => {
  const { token } = useAuth();
  const [installedApps, setInstalledApps] = useState(new Set());
  const [isAppsLoading, setIsAppsLoading] = useState(true);
  const isFetchingRef = useRef(false);

  const fetchInstalledApps = useCallback(async (forceRefresh = false) => {
    if (!token) {
      setInstalledApps(new Set());
      setIsAppsLoading(false);
      return;
    }
    if (isFetchingRef.current && !forceRefresh) return;
    isFetchingRef.current = true;
    if (forceRefresh) setIsAppsLoading(true);

    try {
      const response = await api.get("/apps/my-apps/");
      const apps = response.data.results || response.data;
      setInstalledApps(new Set(apps.map((item) => item.app.technical_name)));
    } catch (error) {
      console.error('[InstalledApps] Error fetching installed apps:', error);
    } finally {
      isFetchingRef.current = false;
      setIsAppsLoading(false);
    }
  }, [token]);

  // Fetch on mount and when token changes (login / logout)
  useEffect(() => {
    setIsAppsLoading(true);
    fetchInstalledApps(true);
  }, [fetchInstalledApps]);

  // Re-fetch when an app is installed / uninstalled via the AppStore or
  // SaaSAdmin pages (they dispatch this custom event).
  useEffect(() => {
    const handleUpdate = () => fetchInstalledApps(true);
    window.addEventListener("company-updated", handleUpdate);
    return () => window.removeEventListener("company-updated", handleUpdate);
  }, [fetchInstalledApps]);

  /**
   * Check whether a module is installed for the current tenant.
   * @param {string} appId  — technical_name of the module (e.g. 'helpdesk')
   * @returns {boolean}
   */
  const isAppInstalled = useCallback(
    (appId) => !appId || installedApps.has(appId),
    [installedApps]
  );

  const contextValue = useMemo(
    () => ({
      installedApps,
      isAppInstalled,
      isAppsLoading,
      refreshInstalledApps: () => fetchInstalledApps(true),
    }),
    [installedApps, isAppInstalled, isAppsLoading, fetchInstalledApps]
  );

  return (
    <InstalledAppsContext.Provider value={contextValue}>
      {children}
    </InstalledAppsContext.Provider>
  );
};

export const useInstalledApps = () => useContext(InstalledAppsContext);

export default InstalledAppsProvider;
